import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

import api from '../../../services/api'
import TextFieldWithAIAtom from '../../atoms/TextFieldWithAI'
import AIDialog from '../../atoms/TextFieldWithAI/AIDialog'

const RECAPTCHA_V3_SITE_KEY = '6LejqscUAAAAAFXNlvS6Drzly056oxGdF6O2oNfW'

const loadRecaptchaV3 = () => {
  const scriptSrc = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_V3_SITE_KEY}`
  if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
    const el = document.createElement('script')
    el.src = scriptSrc
    el.async = true
    el.defer = true
    document.head.insertAdjacentElement('afterbegin', el)
  }
}

const getRecaptchaV3Token = () =>
  new Promise((resolve) => {
    const tryExecute = () => {
      const { grecaptcha } = window
      if (grecaptcha) {
        grecaptcha.ready(() => {
          grecaptcha
            .execute(RECAPTCHA_V3_SITE_KEY, { action: 'generate_message' })
            .then(resolve)
            .catch(() => resolve(null))
        })
      } else {
        setTimeout(tryExecute, 500)
      }
    }
    tryExecute()
  })

const buildMessage = (configMessage, fields, fieldValues) => {
  let message = configMessage.initial || ''
  fields.forEach((field) => {
    const value = fieldValues[field.name]
    if (value && configMessage[field.name]) {
      message += configMessage[field.name].replace(`#${field.name}`, value)
    }
  })
  return message
}

const TextFieldWithAI = ({
  onGenerateMessage,
  fetchConfigURL,
  onGTMButtonClick,
  onGTMDialogGenerate,
  ...props
}) => {
  const { profile } = useSelector((state) => state.user)

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const [fields, setFields] = useState([])
  const [fieldValues, setFieldValues] = useState({})
  const [configId, setConfigId] = useState(null)
  const [configMessage, setConfigMessage] = useState({})
  const [configRule, setConfigRule] = useState('')
  const [textareaMaxLength, setTextareaMaxLength] = useState(500)
  const [isLoadingFields, setIsLoadingFields] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  const [recaptchaV2, setRecaptchaV2] = useState('')
  const [recaptchaVersion, setRecaptchaVersion] = useState('v3')
  const recaptchaV2Ref = useRef()

  useEffect(() => {
    const fetchConfig = async () => {
      setIsLoadingFields(true)
      try {
        const response = await api.get(`/api/v1/ai/configs/${fetchConfigURL}`)
        const config = response.data.data.config

        const fetchedFields = config.value.fields
        setFields(fetchedFields)
        setConfigId(config.id)
        setConfigMessage(config.message)
        setConfigRule(config.rule)
        setTextareaMaxLength(config.characteres || 500)

        const initialValues = fetchedFields.reduce((acc, field) => {
          acc[field.name] =
            field.type === 'SELECT' && field.options?.length
              ? field.options[0].value
              : ''
          return acc
        }, {})
        setFieldValues(initialValues)
      } catch (err) {
        console.error('Erro ao buscar configuração do AiCasei:', err)
      } finally {
        setIsLoadingFields(false)
      }
    }

    fetchConfig()
  }, [fetchConfigURL])

  useEffect(() => {
    if (isDialogOpen) {
      loadRecaptchaV3()
    } else {
      setRecaptchaV2('')
      setRecaptchaVersion('v3')
    }
  }, [isDialogOpen])

  const handleFieldChange = (name, value) => {
    setFieldValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleGenerate = async () => {
    if (recaptchaVersion === 'v2' && !recaptchaV2) return

    onGTMDialogGenerate?.()
    setIsLoading(true)

    try {
      const recaptchaToken =
        recaptchaVersion === 'v3' ? await getRecaptchaV3Token() : recaptchaV2

      const message = buildMessage(configMessage, fields, fieldValues)

      const response = await api.post('/api/v1/ai/chat/ask', {
        message,
        rule: configRule,
        config_id: configId,
        couple_id: profile?.couple_id,
        recaptcha_token: recaptchaToken,
        recaptcha_version: recaptchaVersion,
      })

      if (response.data?.is_robot) {
        setRecaptchaVersion('v2')
        setIsLoading(false)
        return
      }

      if (recaptchaVersion === 'v2') {
        setRecaptchaV2('')
        recaptchaV2Ref.current?.reset()
      }

      setIsLoading(false)

      if (onGenerateMessage) {
        onGenerateMessage(response.data.data.chat.answer.content)
      }
      setIsDialogOpen(false)
    } catch {
      setIsLoading(false)
    }
  }

  return (
    <>
      <TextFieldWithAIAtom
        {...props}
        onAIButtonClick={() => {
          onGTMButtonClick?.()
          setIsDialogOpen(true)
        }}
      />
      <AIDialog
        show={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        fields={fields}
        fieldValues={fieldValues}
        onFieldChange={handleFieldChange}
        onGenerate={handleGenerate}
        isLoading={isLoading}
        isLoadingFields={isLoadingFields}
        textareaMaxLength={textareaMaxLength}
        recaptchaVersion={recaptchaVersion}
        onRecaptchaV2Change={setRecaptchaV2}
        recaptchaV2Ref={recaptchaV2Ref}
      />
    </>
  )
}

TextFieldWithAI.defaultProps = {
  value: '',
  onChange: () => {},
  onBlur: () => {},
  onFocus: () => {},
  invalid: false,
  helperText: '',
  lines: null,
  maxLength: null,
  disabled: false,
  mask: null,
  icon: null,
  variant: 'default',
  onGenerateMessage: null,
  fetchConfigURL: '',
  onGTMButtonClick: null,
  onGTMDialogGenerate: null,
}

TextFieldWithAI.propTypes = {
  label: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  required: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  invalid: PropTypes.bool,
  helperText: PropTypes.string,
  lines: PropTypes.number,
  maxLength: PropTypes.number,
  disabled: PropTypes.bool,
  mask: PropTypes.string,
  icon: PropTypes.element,
  variant: PropTypes.oneOf(['default', 'inverted']),
  onGenerateMessage: PropTypes.func,
  fetchConfigURL: PropTypes.string,
}

export default TextFieldWithAI
